import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import KoliPanel from "@/components/KoliPanel";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Package, Printer } from "lucide-react";
import { toast } from "sonner";

export default function Shipments() {
  const { productById, productLabel } = useData();
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [note, setNote] = useState("");
  const [qtyMap, setQtyMap] = useState({});
  const [koliMap, setKoliMap] = useState({});
  const [koliTypeId, setKoliTypeId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { koliTypes } = useData();
  const koliType = koliTypes.find((k) => k.id === koliTypeId) || koliTypes[0];

  const onKoliTypeChange = (id) => { setKoliTypeId(id); setKoliMap({}); setQtyMap({}); };

  const onKoliChange = (pid, v) => {
    setKoliMap((prev) => ({ ...prev, [pid]: v }));
    const n = parseInt(v) || 0;
    const ratios = koliType?.ratios || {};
    setQtyMap((prev) => {
      const next = { ...prev };
      selectedOrder.items.forEach((it, idx) => {
        if (it.product_id === pid && ratios[it.size]) next[idx] = n ? n * ratios[it.size] : "";
      });
      return next;
    });
  };

  const load = async () => {
    const [o, s, sh] = await Promise.all([api.get("/orders"), api.get("/stock"), api.get("/shipments")]);
    setOrders(o.data); setStock(s.data); setHistory(sh.data);
  };
  useEffect(() => { load(); }, []);

  const openOrders = useMemo(() => orders.filter(o => o.status !== "completed"), [orders]);
  const selectedOrder = useMemo(() => orders.find(o => o.id === selectedOrderId), [orders, selectedOrderId]);

  const stockOf = (it) => {
    const row = stock.find(s => s.product_id === it.product_id && s.size === it.size);
    return row ? row.qty : 0;
  };

  const submit = async () => {
    if (!selectedOrderId) { toast.error("Sipariş seçin"); return; }
    const items = [];
    selectedOrder.items.forEach((it, idx) => {
      const q = parseInt(qtyMap[idx]) || 0;
      if (q > 0) items.push({ product_id: it.product_id, size: it.size, qty: q });
    });
    if (!items.length) { toast.error("En az bir kalem için sevk adedi giriniz"); return; }
    setSubmitting(true);
    try {
      await api.post("/shipments", { order_id: selectedOrderId, note, items, koli_type_id: koliType?.id || "" });
      const total = items.reduce((a, b) => a + b.qty, 0);
      toast.success(`${total} çift sevk edildi — stoktan ve siparişten düşüldü`);
      setSelectedOrderId(""); setNote(""); setQtyMap({}); setKoliMap({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sevkiyat yapılamadı");
    } finally { setSubmitting(false); }
  };

  const totalEntered = Object.values(qtyMap).reduce((a, b) => a + (parseInt(b) || 0), 0);

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="shipments-page">
      <div className="mb-6">
        <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Sevkiyat Çıkışı</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
          <Truck className="w-7 h-7 text-[#D97706]" /> Sevkiyat
        </h1>
        <p className="text-[#6B5B4A] mt-2 text-sm">Siparişi parça parça sevk edin — hem stoktan hem siparişten otomatik düşer</p>
      </div>

      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 mb-6" data-testid="shipment-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">Sipariş Seçin</Label>
            <Select value={selectedOrderId} onValueChange={(v) => { setSelectedOrderId(v); setQtyMap({}); setKoliMap({}); }}>
              <SelectTrigger data-testid="shipment-order-select" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]">
                <SelectValue placeholder="Açık sipariş seçin..." />
              </SelectTrigger>
              <SelectContent className="bg-[#EFE6D6] border-[#DCCFB8] text-[#2B2118]">
                {openOrders.length === 0 && <div className="px-3 py-2 text-sm text-[#8C7B68]">Açık sipariş yok</div>}
                {openOrders.map(o => {
                  const total = o.items.reduce((a, b) => a + b.qty, 0);
                  const shipped = o.items.reduce((a, b) => a + (b.shipped_qty || 0), 0);
                  return (
                    <SelectItem key={o.id} value={o.id} data-testid={`shipment-order-option-${o.id}`}>
                      {o.customer_name} · Kalan: {total - shipped} · {o.delivery_date}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B5B4A]">İrsaliye Notu</Label>
            <Input data-testid="shipment-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Opsiyonel" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
          </div>
        </div>

        {selectedOrder && (
          <KoliPanel items={selectedOrder.items} stockOf={stockOf} koliMap={koliMap} onKoliChange={onKoliChange}
            koliType={koliType} onKoliTypeChange={onKoliTypeChange} />
        )}

        {selectedOrder && (
          <div className="overflow-x-auto rounded-md border border-[#DCCFB8]">
            <table className="w-full text-sm zs-table">
              <thead className="bg-[#EFE6D6]">
                <tr>
                  <th className="text-left px-3 py-2.5">Model Kodu</th>
                  <th className="text-left px-3 py-2.5">Renk</th>
                  <th className="text-left px-3 py-2.5">Taban</th>
                  <th className="text-center px-3 py-2.5">N°</th>
                  <th className="text-right px-3 py-2.5">Kalan Sip.</th>
                  <th className="text-right px-3 py-2.5">Mevcut Stok</th>
                  <th className="text-center px-3 py-2.5">Sevk Adedi</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrder.items.map((it, idx) => {
                  const remaining = it.qty - (it.shipped_qty || 0);
                  if (remaining === 0) return null;
                  const avail = stockOf(it);
                  const max = Math.min(remaining, avail);
                  const p = productById(it.product_id);
                  return (
                    <tr key={`${it.product_id}-${it.size}`} className="border-t border-[#E8DDCA]" data-testid={`shipment-item-row-${idx}`}>
                      <td className="px-3 py-2 font-mono text-[#B45309]">{p?.model_code || "—"}</td>
                      <td className="px-3 py-2 text-[#6B5B4A]">{p?.model_color || "—"}</td>
                      <td className="px-3 py-2 text-[#6B5B4A] text-xs">{p?.sole_code ? `${p.sole_code} / ${p.sole_color}` : "—"}</td>
                      <td className="px-3 py-2 text-center font-mono">{it.size}</td>
                      <td className="px-3 py-2 text-right font-mono text-[#B45309]">{remaining}</td>
                      <td className={`px-3 py-2 text-right font-mono ${avail === 0 ? "text-[#EF4444]" : avail < remaining ? "text-[#B45309]" : "text-[#047857]"}`}>{avail}</td>
                      <td className="px-3 py-2">
                        <Input type="number" min={0} max={max} inputMode="numeric" data-testid={`shipment-qty-${idx}`}
                          value={qtyMap[idx] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value === "" ? "" : Math.max(0, Math.min(max, parseInt(e.target.value) || 0));
                            setQtyMap(prev => ({ ...prev, [idx]: v }));
                          }}
                          className="zs-size-input text-center h-10 w-24 mx-auto bg-[#FFFBF4] border-[#DCCFB8]" placeholder="0" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedOrder && (
          <div className="flex items-center justify-between mt-5">
            <div>
              <p className="text-[10px] font-mono uppercase text-[#6B5B4A] tracking-widest">Sevk Toplam</p>
              <p className="font-mono text-2xl font-bold text-[#047857]" data-testid="shipment-total">{totalEntered} çift</p>
            </div>
            <Button onClick={submit} disabled={submitting || totalEntered === 0} data-testid="shipment-submit-btn"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              <Truck className="w-4 h-4 mr-2" />
              {submitting ? "Sevk ediliyor..." : "Sevkiyatı Oluştur"}
            </Button>
          </div>
        )}
      </div>

      <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5" data-testid="shipment-history">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
          <Package className="w-5 h-5 text-[#B45309]" /> Sevkiyat Geçmişi
        </h2>
        {history.length === 0 && <p className="text-sm text-[#8C7B68]">Henüz sevkiyat yok</p>}
        <div className="space-y-3">
          {history.map((s) => {
            const total = s.items.reduce((a, b) => a + b.qty, 0);
            return (
              <div key={s.id} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-4" data-testid={`shipment-history-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{s.customer_name || "Müşteri"}</p>
                    <p className="text-[11px] font-mono text-[#8C7B68] uppercase tracking-widest">{new Date(s.created_at).toLocaleString("tr-TR")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xl font-bold text-[#047857]">−{total} çift</span>
                    <a href={`/irsaliye/${s.id}`} target="_blank" rel="noreferrer" data-testid={`shipment-waybill-${s.id}`}
                      className="flex items-center gap-1 text-xs font-display uppercase tracking-wide px-3 h-9 rounded-md border border-[#DCCFB8] text-[#B45309] hover:bg-[#FFFBF4]">
                      <Printer className="w-3.5 h-3.5" /> İrsaliye
                    </a>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {s.items.map((it) => (
                    <span key={`${it.product_id}-${it.size}`} className="text-[11px] font-mono px-2 py-1 rounded bg-[#FFFBF4] border border-[#DCCFB8] text-[#6B5B4A]">
                      {productLabel(productById(it.product_id))} · N°{it.size} · <b className="text-[#2B2118]">{it.qty}</b>
                    </span>
                  ))}
                </div>
                {s.note && <p className="text-xs text-[#8C7B68] mt-2 italic">&ldquo;{s.note}&rdquo;</p>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
